import * as React from 'react';

import { SmallHeading, NewWindowIcon } from '@circleci/compass';
import { Hit } from 'react-instantsearch-core';
import { connectHighlight } from 'react-instantsearch-dom';

import { Action, LibContext, PlatformName } from '~/contexts/LibContext';
import { styled } from '~/styled';

import { Snippet, Highlight } from './SearchMarks';

const isInternalLink = (platformName: PlatformName, link: string): boolean => {
  switch (platformName) {
    case 'Docs':
      return link.startsWith('https://circleci.com/docs');
    case 'Developer':
      return link.startsWith('https://circleci.com/developer');
    default:
      return false;
  }
};

const Result = styled.a`
  text-decoration: none;
`;

const ResultTitle = styled(SmallHeading)(({ theme }) => {
  return `
    margin-top: ${theme.space.space40};
    font-size: 18px; // not in theme
    font-weight: 600; // not in theme
    margin-bottom: ${theme.space.space12};
  `;
});

const ResultLinkIcon = styled(NewWindowIcon)(({ theme }) => {
  return `
    display: inline-block;
    margin-left: ${theme.space.space4};
    margin-bottom: -${theme.space.space2};
  `;
});

const ResultContext = styled.p(({ theme }) => {
  return `
    font-size: ${theme.fontSizes.fontsize14};
    margin: ${theme.space.space12} 0;
    color: #898989; // not in theme
  `;
});

const ResultContent = styled.p(({ theme }) => {
  return `
    margin: 0;
    color: ${theme.colors.n500};
    line-height: ${theme.lineHeights.lineheight24};
  `;
});

const ResultTag = styled.span(({ theme }) => {
  return `
    margin-top: ${theme.space.space12};
    background-color: ${theme.colors.white};
    border: 0.5px solid #d8d8d8; // not in theme
    color: ${theme.colors.n700};
    font-size: ${theme.fontSizes.fontsize12};
    font-weight: ${theme.fontWeights.medium};
    letter-spacing: 1.7px; // not in theme
    padding: ${theme.space.space4} ${theme.space.space8};
    text-transform: uppercase;
    display: inline-block;
  `;
});

// https://www.algolia.com/doc/api-reference/widgets/snippet/react/#connector
const SearchSnippetMark = connectHighlight(Snippet);

//www.algolia.com/doc/api-reference/widgets/highlight/react/#connector
const SearchHighlightMark = connectHighlight(Highlight);

interface Props {
  title: JSX.Element;
  description: JSX.Element;
  contextLabel?: string;
  context?: string | null;
  tag?: string | null;
  link: string;
}

const SearchResultItem = ({
  title,
  description,
  contextLabel,
  context,
  tag,
  link,
}: Props): JSX.Element => {
  const { notifyTrackingFired, platformName } = React.useContext(LibContext);

  const externalIcon = !isInternalLink(platformName, link);

  const item = (
    <Result
      href={link}
      rel="noopener noreferrer"
      data-testid="search-result-item"
      onClick={() => {
        return (
          notifyTrackingFired &&
          notifyTrackingFired('search-result-item', Action.clicked, {
            link,
          })
        );
      }}
    >
      <ResultTitle>
        {title}
        {externalIcon && <ResultLinkIcon size={18} />}
      </ResultTitle>
      {context && (
        <ResultContext>
          {contextLabel && <strong>{contextLabel}: </strong>}
          {context}
        </ResultContext>
      )}
      <ResultContent>{description}</ResultContent>
      {tag && <ResultTag>{tag}</ResultTag>}
    </Result>
  );

  return item;
};

export interface DocResultHit {
  title: string;
  content: string;
  headings: string[];
  url: string;
  anchor?: string;
}

export const DocResultItem = ({ hit }: { hit: Hit<DocResultHit> }) => {
  return (
    <SearchResultItem
      title={<SearchSnippetMark hit={hit} attribute={'title'} />}
      description={<SearchSnippetMark hit={hit} attribute={'content'} />}
      context={hit.headings ? hit.headings.join(' > ') : null}
      link={
        hit.anchor
          ? `https://circleci.com/docs${hit.url}#${hit.anchor}`
          : `https://circleci.com/docs${hit.url}`
      }
    />
  );
};

export interface OrbResultHit {
  full_name: string;
  description: string;
  last_updated_at: {
    formatted: string;
  };
  is_certified: boolean;
  is_partner: boolean;
  url: string;
}

export const OrbResultItem = ({ hit }: { hit: Hit<OrbResultHit> }) => {
  return (
    <SearchResultItem
      title={<SearchHighlightMark hit={hit} attribute={'full_name'} />}
      description={<SearchHighlightMark hit={hit} attribute={'description'} />}
      contextLabel="Version Published"
      context={hit.last_updated_at.formatted}
      tag={hit.is_partner ? 'Partner' : hit.is_certified ? 'Certified' : null}
      link={hit.url}
    />
  );
};

export interface ImgResultHit {
  name: string;
  description: {
    en: string;
  };
  latestVersion: string;
  repo: string;
}

export const ImgResultItem = ({ hit }: { hit: Hit<ImgResultHit> }) => {
  return (
    <SearchResultItem
      title={<SearchHighlightMark hit={hit} attribute={'name'} />}
      description={
        <SearchHighlightMark hit={hit} attribute={'description.en'} />
      }
      contextLabel="Latest version"
      context={hit.latestVersion}
      link={`https://circleci.com/developer/images/image/${hit.name}`}
    />
  );
};
